/**
 * Copyright 2021-2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type CategoryData, type CategoryDataAces } from '@/features/category';
import { isBrandShelfPage } from '@/utils/isBrandShelfPage';
import type {
  ShelfHead,
  ShelfLeftNav,
  ShelfPageBodyComplete,
  ShelfPageBodyItemGeneric,
  ShelfResultsBottomItemTwo,
} from '@/types/legacy';
import { type ProductShelfACESResultsModel } from '@/api/types/browse-search-types';

type NormalizeShelfResponseParams = {
  data: CategoryData | CategoryDataAces | undefined;
  pathname: string;
  acesEnabled?: boolean;
  isMerchShelfPage?: boolean;
};

type NormalizedShelfResponse = {
  catLabel?: string | undefined;
  productId?: null | string;
  brandName?: string;
  shelfLeftNav: ShelfLeftNav | undefined;
  shelfResultBottom:
    | [ShelfPageBodyItemGeneric, ShelfResultsBottomItemTwo]
    | Array<{ '@type': string }>;
  shelfResultHeader: ShelfPageBodyItemGeneric[];
  shelfPageBody: ShelfPageBodyComplete;
  shelfHead: ShelfHead[];
};

export const normalizeShelfResponse = ({
  data,
  pathname,
  acesEnabled,
  isMerchShelfPage,
}: NormalizeShelfResponseParams): NormalizedShelfResponse => {
  const result: NormalizedShelfResponse = {
    catLabel: '',
    shelfLeftNav: undefined,
    shelfResultBottom: [],
    shelfResultHeader: [],
    shelfPageBody: [],
    shelfHead: [],
  };

  if (!data) {
    return result;
  }

  if (isBrandShelfPage(pathname)) {
    result.brandName = undefined;
    result.catLabel = undefined;

    return result;
  }

  const productOrMerchShelfResults =
    acesEnabled && isMerchShelfPage
      ? (data as CategoryDataAces).merchShelfResults
      : data.productShelfResults;

  if (isCategoryData(data)) {
    result.catLabel = '';
    if (
      productOrMerchShelfResults &&
      ('partGroupId' in productOrMerchShelfResults ||
        'partTerminologyId' in productOrMerchShelfResults)
    ) {
      result.productId =
        productOrMerchShelfResults.partGroupId ??
        (productOrMerchShelfResults as ProductShelfACESResultsModel).partTerminologyId;
    } else {
      result.productId = data.productId;
    }
    result.shelfLeftNav =
      data.contents?.contents[0].shelfLeftNav ?? ([] as unknown as ShelfLeftNav);
    result.shelfResultBottom = data.contents?.contents[0].shelfResultBottom ?? [];
    result.shelfResultHeader = data.contents?.contents[0].shelfResultHeader ?? [];
    result.shelfPageBody = data.contents?.contents[0].shelfPageBody ?? [];
    result.shelfHead = data.contents?.contents[0].shelfHead ?? [];
  }

  return result;
};

const isCategoryData = (
  item: CategoryData | CategoryDataAces,
): item is CategoryData | CategoryDataAces => {
  return Boolean(
    typeof item === 'object' &&
    item.contents &&
    typeof item.contents === 'object' &&
    Array.isArray(item.contents.contents) &&
    item.contents.contents[0] &&
    typeof item.contents.contents[0] === 'object' &&
    'shelfPageBody' in item.contents.contents[0],
  );
};
